import { Image, Picture, Box, Flex, Typography } from "@jewlr/storybook/core"
import React from "react"
import styled from "styled-components"

import { IS_UK, IS_US, jewlr_t } from "helpers/application"

const Video = styled.video`
  width: 100%;
  ${(props) => props.theme.mediaQueries.tablet`
    width: 60%;
    margin: 0 auto;
  `}
`
const DiamondTitle = styled(Typography).attrs({
  as: "h2",
  fontSize: {
    _: "1.5em",
    desktop: "2.0em",
    tablet: "1.0em",
    widescreen: "2.25em",
  },
  fontWeight: "normal",
})``

const MetalBox = styled(Box).attrs((props) => ({
  mb: 2,
  p: 3,
  ...props,
}))`
  background-color: ${(props) => props.theme.colors.white};
  height: 100%;
`

const Materials = () => {
  const is_uk = IS_UK()
  const is_us = IS_US()

  return (
    <Box contain lineHeight="copy" mb={1} overflow="hidden" pb={1}>
      <Box bg="primaryColors.oldlace" mb={1} textAlign="center">
        <Typography
          as="h1"
          display={{ _: "none", tablet: "block" }}
          fontWeight="normal"
          pt={4}
          px="32px"
          variant="h1"
        >
          What&apos;s in it?
        </Typography>
        <Typography
          as="h1"
          display={{ _: "none", tablet: "block" }}
          fontWeight="normal"
          mt="-14px"
          variant="h1"
        >
          (and how to care for them!)
        </Typography>
        <Typography
          as="h1"
          display={{ _: "block", tablet: "none" }}
          fontWeight="normal"
          lineHeight={{ _: "38px", tablet: "16px" }}
          pt={4}
          px="32px"
          variant="h1"
        >
          What&apos;s in it? (and how to care for them!)
        </Typography>
        <Typography
          as="p"
          fontSize={{ _: "1.2em", tablet: "1.5em" }}
          mt={2}
          pb={{ _: "48px" }}
          px={{ _: 1, widescreen: "13em" }}
          variant="body1"
        >
          Making your {jewlr_t("terms.jewelry")} ourselves means that we can
          guarantee ethically sourced materials and fair working conditions. We
          use recycled gold and silver, lab diamonds, and conflict-free diamonds
          and gemstones. Proper cleaning and storage of each piece is the key to
          keeping it beautiful!
        </Typography>

        <Box
          alignItems="center"
          display={{ _: "block", tablet: "flex" }}
          flexDirection={{ tablet: "row-reverse" }}
          pointer
          position={{ tablet: "relative" }}
          pt={{ tablet: "24px" }}
        >
          <Flex
            flexDirection={{ _: "column", tablet: "row" }}
            gutter={1}
            justifyContent={"center"}
          >
            <Box pb={{ tablet: "0px" }} position="relative" width="100%">
              <Picture
                alt="Metals"
                fallback={require("images/leefiori/info/materials-info-top-banner-2x.jpg")}
                srcSet={[
                  {
                    media: "(max-width: 767px)",
                    sizes: "376vw",
                    srcSet: `
                          ${require("images/leefiori/info/mobile-materials-info-top-banner.jpg")} 376w,
                          ${require("images/leefiori/info/mobile-materials-info-top-banner-2x.jpg")} 752w
              `,
                  },
                  {
                    media: "(min-width: 768px)",
                    sizes: "1366vw",
                    srcSet: `
                          ${require("images/leefiori/info/materials-info-top-banner.jpg")} 1366w,
                          ${require("images/leefiori/info/materials-info-top-banner-2x.jpg")} 2732w
              `,
                  },
                ]}
                width="100%"
              />

              <Image
                alt="Metals"
                left={{
                  _: "217px",
                  desktop: "149px",
                  tablet: "117px",
                  widescreen: "318px",
                }}
                position="absolute"
                src={require("images/leefiori/info/title-metals.svg")}
                top={{
                  _: "-29px",
                  desktop: "-32px",
                  tablet: "-22px",
                  widescreen: "-48px",
                }}
                top-left="40%"
                width={{ _: "34%", tablet: "20%", widescreen: "15%" }}
              />
            </Box>
          </Flex>
        </Box>
        <Box mb={2} mt={{ _: 2, tablet: 8 }} pt={{ _: 2 }}>
          <Flex
            flexDirection={{ _: "column", tablet: "row" }}
            gutter={{ tablet: 2 }}
            justifyContent="space-around"
            ml="auto"
            mr="auto"
            width={{ _: "95%", tablet: "90%" }}
          >
            <Box flex="1">
              <MetalBox px={{ tablet: "26px" }}>
                <Typography
                  as="h2"
                  fontWeight="normal"
                  mt={{ _: 0, tablet: "20px" }}
                  pt={{ _: "32px", tablet: 0 }}
                  variant="h2"
                >
                  Gold
                </Typography>
                <Typography
                  fontSize={{ _: "14px" }}
                  pb={{ _: "8px", tablet: "16px", widescreen: "8px" }}
                  variant="body4"
                >
                  Our solid gold is available in{" "}
                  {is_uk ? "9kt or 14kt" : "10K or 14K"} yellow, white or rose
                  gold. Its beautiful luster creates a rich background for
                  sparkling gems. Gold’s durability makes it perfect for
                  everyday wear, and over time it develops a soft patina.
                </Typography>
                <Typography
                  as="p"
                  fontSize={{ _: "14px" }}
                  pb={{ _: "32px", tablet: "74px", widescreen: "0px" }}
                  //pt={{ tablet: "36px" }}
                  variant="body4"
                >
                  Keep your gold looking its best by cleaning in warm water with
                  dish soap. Soak, then gently scrub with a clean, soft
                  toothbrush. Rinse in warm water and pat dry. Store
                  individually in its pouch to prevent scratching.
                </Typography>
              </MetalBox>
            </Box>
            <Box flex="1">
              <MetalBox>
                <Typography
                  as="h2"
                  fontWeight="normal"
                  mt={{ _: 0, tablet: "20px" }}
                  pt={{ _: "32px", tablet: 0 }}
                  variant="h2"
                >
                  Silver
                </Typography>
                <Typography
                  as="p"
                  fontSize={{ _: "14px" }}
                  pb={{
                    _: "8px",
                    desktop: "16px",
                    tablet: "32px",
                    widescreen: "8px",
                  }}
                  variant="body4"
                >
                  Sterling silver is the most affordable precious metal and has
                  a bright white shine. It’s one of the most popular precious
                  metals for {jewlr_t("terms.jewelry")}. Sterling silver is
                  perfect for dainty pieces or bold shapes and styles.
                </Typography>
                <Typography
                  as="p"
                  fontSize={{ _: "14px" }}
                  pb={{
                    _: "32px",
                    desktop: "24px",
                    tablet: "16px",
                    widescreen: "0px",
                  }}
                  pt={{ desktop: "24px", widescreen: "0px" }}
                  variant="body4"
                >
                  Keep your silver looking its best by wearing it often to keep
                  tarnish at bay. Avoid chemicals – including chlorine in
                  swimming pools, lotions, cosmetics and fragrances that can
                  cause tarnishing. After wearing, wash in warm water and pat
                  dry. Store individually in its pouch to prevent scratching.
                </Typography>
              </MetalBox>
            </Box>
            <Box flex="1">
              <MetalBox>
                <Typography
                  as="h2"
                  fontWeight="normal"
                  mt={{ _: 0, tablet: "20px" }}
                  pt={{ _: "32px", tablet: 0 }}
                  variant="h2"
                >
                  {is_uk ? "18kt Gold Vermeil" : "Vermeil"}
                </Typography>
                {is_uk ? (
                  <Box>
                    <Typography
                      as="p"
                      fontSize={{ _: "14px" }}
                      pb={{
                        _: "8px",
                        desktop: "24px",
                        tablet: "16px",
                        widescreen: "8px",
                      }}
                      variant="body4"
                    >
                      Our 18kt Gold Vermeil on Sterling Silver consists of a
                      layer of 18kt yellow gold 2.5 microns thick over solid
                      sterling silver. Create bold pieces with the look of gold
                      at a fraction of the cost, for the ultimate balance
                      between fine and fashion {jewlr_t("terms.jewelry")}.
                    </Typography>
                    <Typography
                      as="p"
                      fontSize={{ _: "14px" }}
                      pb={{ _: "32px", tablet: "32px", widescreen: "0px" }}
                      variant="body4"
                    >
                      Keep your gold plated vermeil looking its best with a
                      little TLC. Avoid physical activities that can scratch the
                      surface, and chemicals – including chlorine pools,
                      lotions, cosmetics, and fragrances. After wearing, wipe
                      with a soft, non-abrasive cloth. Store individually in its
                      pouch to prevent scratching.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography
                      as="p"
                      fontSize={{ _: "14px" }}
                      pb={{
                        _: "8px",
                        desktop: "24px",
                        tablet: "16px",
                        widescreen: "8px",
                      }}
                      variant="body4"
                    >
                      Our vermeil consists of a layer of 18K yellow gold 2.5
                      microns thick over solid sterling silver. Create bold
                      pieces with the look of gold at a fraction of the cost,
                      for the ultimate balance between fine and fashion{" "}
                      {jewlr_t("terms.jewelry")}.
                    </Typography>
                    <Typography
                      as="p"
                      fontSize={{ _: "14px" }}
                      pb={{ _: "48px", tablet: "32px", widescreen: "0px" }}
                      variant="body4"
                    >
                      Keep your vermeil looking its best with a little TLC.
                      Avoid physical activities that can scratch the surface,
                      and chemicals – including chlorine pools, lotions,
                      cosmetics, and fragrances. After wearing, wipe with a
                      soft, non-abrasive cloth. Store individually in its pouch
                      to prevent scratching.
                    </Typography>
                  </Box>
                )}
              </MetalBox>
            </Box>
          </Flex>
        </Box>
        <Box
          mb={{ _: 9, tablet: 4 }}
          pt={{ _: "48px", tablet: "0px" }}
          px={{ tablet: "2em" }}
        >
          <Box
            bg="primaryColors"
            m={{ widescreen: 6 }}
            mt={{ widescreen: "0px" }}
            p={1}
            px={{ widescreen: "2em" }}
          >
            <Flex
              flexDirection={{ _: "column", tablet: "row-reverse" }}
              gutter={1}
              justifyContent="space-between"
            >
              <Box mb={4} mt={{ tablet: 8 }} position="relative" width="100%">
                <Flex
                  flexDirection={{ _: "column", tablet: "row" }}
                  gutter={1}
                  justifyContent={"center"}
                >
                  <Box mt={{ tablet: "16px" }} position="relative" width="100%">
                    <Picture
                      alt="Diamonds"
                      fallback={require("images/leefiori/info/materials-info-top-banner-2x.jpg")}
                      srcSet={[
                        {
                          media: "(max-width: 767px)",
                          sizes: "376vw",
                          srcSet: `
                          ${require("images/leefiori/info/mobile-materials-info-diamond-banner.jpg")} 376w,
                          ${require("images/leefiori/info/mobile-materials-info-diamond-banner-2x.jpg")} 752w
              `,
                        },
                        {
                          media: "(min-width: 768px)",
                          sizes: "1366vw",
                          srcSet: `
                          ${require("images/leefiori/info/materials-info-diamond-banner.jpg")} 1366w,
                          ${require("images/leefiori/info/materials-info-diamond-banner-2x.jpg")} 2732w
              `,
                        },
                      ]}
                      width="100%"
                    />

                    <Image
                      alt="Diamonds"
                      left={{
                        _: "44px",
                        desktop: "278px",
                        tablet: "188px",
                        widescreen: "335px",
                      }}
                      position="absolute"
                      src={require("images/leefiori/info/Diamonds.svg")}
                      top={{
                        _: "-25px",
                        desktop: "61px",
                        tablet: "36px",
                        widescreen: "61px",
                      }}
                      top-left="60%"
                      width={{ tablet: "50%" }}
                    />
                  </Box>
                </Flex>
              </Box>

              <Box
                m={1}
                mt={{ _: "0px", desktop: "134px", tablet: "100px" }}
                p={{ _: "0px", widescreen: 2 }}
                pr={{ _: 2, tablet: "0px", widescreen: 3 }}
                width={{ tablet: "80%" }}
              >
                <DiamondTitle>Diamonds</DiamondTitle>
                <Typography
                  as="p"
                  fontSize={{
                    desktop: "1.0em",
                    tablet: "0.8em",
                    widescreen: "1.0em",
                  }}
                  mt={{ _: 16, tablet: "0px", widescreen: 16 }}
                  variant="body3"
                >
                  Our gemologists have selected white diamonds for their best
                  combinations of colour, clarity, and cut – guaranteed to
                  sparkle! We use white lab diamonds wherever we can. They offer
                  excellent value and are chemically, physically, and optically
                  identical to natural diamonds. So you can get more for less!
                </Typography>
                <DiamondTitle
                  mt={{ _: 2, widescreen: "40px" }}
                  pt={{ desktop: "10px" }}
                >
                  Grey Diamonds
                </DiamondTitle>
                <Typography
                  as="p"
                  fontSize={{
                    desktop: "1.0em",
                    tablet: "0.8em",
                    widescreen: "1.0em",
                  }}
                  mt={{ _: 16, tablet: "0px", widescreen: 16 }}
                  variant="body3"
                >
                  Our grey diamonds are ethically sourced natural diamonds that
                  have characteristics acquired during formation deep within the
                  Earth. Each one is naturally unique and has a dramatic look.
                </Typography>
                <DiamondTitle
                  mt={{ _: 2, widescreen: "40px" }}
                  pt={{ desktop: "10px" }}
                >
                  Black Diamonds
                </DiamondTitle>
                <Typography
                  as="p"
                  fontSize={{
                    desktop: "1.0em",
                    tablet: "0.8em",
                    widescreen: "1.0em",
                  }}
                  mt={{ _: 15, tablet: "0px", widescreen: 15 }}
                  pr={{ _: 2, tablet: 0 }}
                  variant="body3"
                >
                  Our black diamonds are ethically sourced natural diamonds that
                  are completely opaque. Like all other diamonds, their adamant
                  surface quality reflects light like nothing else! Black
                  diamonds create a dramatic look in any{" "}
                  {jewlr_t("terms.jewelry")} setting.
                </Typography>
                <DiamondTitle
                  mt={{ _: 2, widescreen: "40px" }}
                  pt={{ desktop: "10px" }}
                >
                  Caring For Your Diamonds
                </DiamondTitle>
                <Typography
                  as="p"
                  fontSize={{
                    desktop: "1.0em",
                    tablet: "0.8em",
                    widescreen: "1.0em",
                  }}
                  mt={{ _: 15, tablet: "0px", widescreen: 15 }}
                  pr={{ _: 2, tablet: 0 }}
                  variant="body3"
                >
                  Keep your diamonds looking their sparkliest by cleaning in
                  warm water with dish soap. Soak, then gently scrub with a
                  clean, soft toothbrush. Rinse in warm water and pat dry. Store
                  individually in its pouch to prevent scratching.
                </Typography>
              </Box>
            </Flex>
          </Box>
        </Box>

        <Box mb={{ _: 2, tablet: 8 }} px={{ _: "1em", widescreen: "6em" }}>
          <Flex
            flexDirection={{ _: "column", tablet: "row-reverse" }}
            gutter={1}
            justifyContent="space-evenly"
          >
            <Box
              bg={{ _: "white", tablet: "primaryColors.oldlace" }}
              pl={{ _: "30px", desktop: 5 }}
              pr={{ _: 2, desktop: 5, tablet: "0px" }}
              width={{ tablet: "90%" }}
            >
              <DiamondTitle
                mt={{ _: "2px", widescreen: "3px" }}
                pt={{ _: 7, desktop: "10px" }}
              >
                Rubies & Sapphires
              </DiamondTitle>
              <Typography
                as="p"
                fontSize={{
                  desktop: "1.0em",
                  tablet: "0.8em",
                  widescreen: "1.0em",
                }}
                mt={{ _: 15, tablet: "0px", widescreen: 15 }}
                pl={{ _: 0, tablet: "0px" }}
                variant="body3"
              >
                Rubies and blue sapphires are not only beautiful – they are the
                hardest gemstones, which makes them durable enough for everyday
                wear. Historically they’ve adorned the crowns of royalty, so
                it’s safe to say they’re a classic. White sapphires are a more
                affordable alternative to diamonds.
              </Typography>
              <Typography
                as="p"
                fontSize={{
                  desktop: "1.0em",
                  tablet: "0.8em",
                  widescreen: "1.0em",
                }}
                mt={{ _: 16, tablet: "0px", widescreen: 16 }}
                pb={{ _: 2, tablet: 0 }}
                pl={{ _: 0, tablet: "0px" }}
                //pt={{ desktop: "20px" }}

                variant="body3"
              >
                Keep your rubies and sapphires looking their best by soaking
                them in warm water with dish soap and gently scrubbing with a
                soft brush. Rinse in warm water, pat dry, and store each in its
                pouch to prevent scratching.
              </Typography>
            </Box>
            <Box
              m={{ _: "16px 0 27px", tablet: "45px 16px 0" }}
              width={{ tablet: "100%" }}
            >
              <Box
                alignItems="center"
                display={{ _: "block", tablet: "flex" }}
                flexDirection={{ tablet: "row-reverse" }}
                pointer
                position={{ tablet: "relative" }}
              >
                <Flex
                  flexDirection={{ _: "column", tablet: "row" }}
                  gutter={1}
                  justifyContent={"center"}
                >
                  <Box position="relative" width="100%">
                    <Picture
                      alt="Precious Gemstone"
                      fallback={require("images/leefiori/info/materials-info-gemstone-2x.jpg")}
                      srcSet={[
                        {
                          media: "(max-width: 767px)",
                          sizes: "376vw",
                          srcSet: `
                          ${require("images/leefiori/info/mobile-materials-info-gemstone.jpg")} 376w,
                          ${require("images/leefiori/info/mobile-materials-info-gemstone-2x.jpg")} 752w
              `,
                        },
                        {
                          media: "(min-width: 768px)",
                          sizes: "885vw",
                          srcSet: `
                          ${require("images/leefiori/info/materials-info-gemstone.jpg")} 885w,
                          ${require("images/leefiori/info/materials-info-gemstone-2x.jpg")} 1770w
              `,
                        },
                      ]}
                      width="100%"
                    />

                    <Image
                      alt="Precious Gemstone"
                      bottom={{ _: "590px", tablet: "0px" }}
                      left={{ _: "46px", tablet: "18%" }}
                      position="absolute"
                      src={require("images/leefiori/info/desktop-precious-gemstones.svg")}
                      top={{ tablet: "-13%" }}
                      top-left={{ _: "60px", tablet: "60%" }}
                      width={{ _: "74%", tablet: "69%" }}
                    />
                  </Box>
                </Flex>
              </Box>
            </Box>
          </Flex>
        </Box>
        <Box pb={{ _: 4, tablet: 8 }}>
          <Typography
            as="h2"
            fontWeight="normal"
            lineHeight={{ _: "38px", tablet: "16px" }}
            pb={4}
            variant="h1"
          >
            How To Care For Your Fine {jewlr_t("terms.Jewelry")}
          </Typography>

          <Video
            autoPlay
            className="lazyload"
            loop
            muted
            playsInline
            poster={require("images/leefiori/info/how-to-clean.png")}
          >
            <source
              src={`${
                sl.config.jewlr_asset_url
              }lee_fiori_videos/2020/how-to-clean${is_us ? "-us" : ""}.mp4`}
              type="video/mp4"
            />
          </Video>
        </Box>
      </Box>
    </Box>
  )
}
export default Materials
